// Angular
import { AfterViewInit, Component, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NgClass, DatePipe } from '@angular/common';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Angular Material
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';

// Third Party
import {
  filter,
  finalize,
  merge,
  of,
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  tap,
} from 'rxjs';

// Services
import { ReleaseNotesService } from '@services/release-notes.service';

// Models
import { ReleaseNoteResponse } from '@components/release-notes';

// Constants
import { Action, Status } from '@constants/enum';
import { DEBOUNCE_TIME_SEARCH_FIELDS } from '@constants/common';
import { CustomPaginator } from '@utils/custom-paginator';

// Pipes
import { FormatProductNamePipe } from '@utils/format-product-name.pipe';

// Dialogs
import { DeleteDialogComponent, ConfirmDialogComponent } from '@components/dialogs';

// Components
import { LoadingSpinnerComponent, FormErrorComponent } from '@components/common-components';

@Component({
  selector: 'app-release-notes-table',
  imports: [
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSidenavModule,
    MatTooltipModule,
    ReactiveFormsModule,
    RouterLink,
    NgClass,
    FormatProductNamePipe,
    DatePipe,
    LoadingSpinnerComponent,
    FormErrorComponent,
  ],
  templateUrl: './release-notes-table.component.html',
  styleUrl: './release-notes-table.component.scss',
  providers: [{ provide: MatPaginatorIntl, useValue: CustomPaginator() }],
})
export class ReleaseNotesTableComponent implements AfterViewInit {
  private matDialog = inject(MatDialog);
  private readonly releaseNotesService = inject(ReleaseNotesService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly action = Action;
  readonly status = Status;
  readonly displayedColumns = [
    'productId',
    'tentativeReleaseAt',
    'title',
    'version',
    'publishedAt',
    'modifiedBy',
    'edit',
    'status',
  ];

  dataSource = new MatTableDataSource();
  readonly resultsLength = signal(0);
  readonly search = signal('');
  readonly loading = signal(true);
  readonly error = signal(false);
  readonly paginator = viewChild.required(MatPaginator);
  readonly sort = viewChild.required(MatSort);
  public subjReload = new Subject<void>();
  searchControl = new FormControl<string>('');

  ngAfterViewInit() {
    this.paginator().pageSize = 50;
    this.sort().disableClear = true;
    merge(
      this.sort().sortChange.pipe(
        tap(() => {
          this.paginator().pageIndex = 0;
        }),
      ),
      this.paginator().page,
      this.searchControl.valueChanges.pipe(
        debounceTime(DEBOUNCE_TIME_SEARCH_FIELDS),
        map((value) => value.trim().toLowerCase()),
        distinctUntilChanged(),
        tap((value) => {
          this.search.set(value);
          this.paginator().pageIndex = 0;
        }),
      ),
      this.subjReload,
    )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(20),
        switchMap(() => {
          this.loading.set(true);
          return this.releaseNotesService
            .getAllReleaseNotes(
              this.paginator().pageIndex,
              this.paginator().pageSize,
              this.sort().active,
              this.sort().direction.toUpperCase(),
              this.search(),
            )
            .pipe(
              catchError(() => {
                this.loading.set(false);
                this.error.set(true);
                return of({
                  items: [],
                  page: 1,
                  totalCount: 0,
                });
              }),
            );
        }),
      )
      .pipe(
        tap((data: ReleaseNoteResponse) => {
          this.resultsLength.set(data.totalCount);
          this.dataSource.data = data.items;
          this.error.set(false);
          this.loading.set(false);
        }),
      )
      .subscribe({
        error: () => this.error.set(true),
      });

    this.subjReload.next();
  }

  navigateToReleaseNotes(type: string, id: string) {
    this.router.navigate(['/release-notes', type, id]);
  }

  openDeleteDialog(element) {
    const dialogRef = this.matDialog.open(DeleteDialogComponent, {
      data: {
        title: 'Are you sure?',
        message: `Please confirm you wish to delete release notes '${element.title}'.`,
        paragraph: 'This cannot be undone.',
        buttonConfirm: 'Yes, Delete',
        buttonCancel: 'No, Cancel',
      },
    });
    dialogRef
      .afterClosed()
      .pipe(
        filter((data) => !!data),
        tap(() => this.loading.set(true)),
        switchMap(() =>
          this.releaseNotesService.deleteById(element.releaseId).pipe(
            tap(() => this.subjReload.next()),
            finalize(() => this.loading.set(false)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: () => this.error.set(true),
      });
  }

  openPublishDialog(releaseId: string) {
    const dialogRef = this.matDialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Publish Release Notes?',
        message:
          'Publishing will make these release notes live for all users. Make sure everything looks right before confirming.',
        buttonConfirm: 'Yes, Publish',
        buttonCancel: 'No, Cancel',
      },
    });
    dialogRef
      .afterClosed()
      .pipe(
        filter((data) => !!data),
        tap(() => this.loading.set(true)),
        switchMap(() =>
          this.releaseNotesService.publishById(releaseId).pipe(
            tap(() => this.subjReload.next()),
            finalize(() => this.loading.set(false)),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        error: () => this.error.set(true),
      });
  }
}
